// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { NavigationItem } from '@/constants/navigation-sections';
import { NavigationContext } from '@/contexts/navigation-context';
import { isCurrentPath } from '@/utilities/navigation';
import { useLocation } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

interface NavigationProviderProps {
  children: React.ReactNode;
  navigationSections: {
    label: string;
    items: NavigationItem[];
    additionalPaths?: string[];
  }[];
}

/**
 * Provider component that manages the active navigation section state.
 * Tracks user's selected section and initializes from current path on first load.
 */
export function NavigationProvider(props: NavigationProviderProps) {
  const { children, navigationSections } = props;
  const location = useLocation();
  const [activeSection, setActiveSectionState] = useState<string | null>(null);
  const [activeSubsection, setActiveSubsectionState] = useState<string | null>(
    null
  );
  const [hasInitialized, setHasInitialized] = useState(false);

  /**
   * Determines which section and subsection contain the given path
   */
  const getSectionAndSubsectionForPath = useCallback(
    (targetLocation: typeof location) => {
      for (const section of navigationSections) {
        const itemPaths = section.items.map((item) => item.to);
        const allPaths = [...itemPaths, ...(section.additionalPaths || [])];

        // Check if this section matches
        if (allPaths.some((path) => isCurrentPath(path, targetLocation))) {
          // Find the specific subsection that matches
          const matchingItem = section.items.find((item) =>
            isCurrentPath(item.to, targetLocation)
          );
          return {
            section: section.label,
            subsection: matchingItem?.label || null,
          };
        }
      }
      return { section: null, subsection: null };
    },
    [navigationSections]
  );

  /**
   * Initialize active section and subsection from current path on first load
   */
  useEffect(() => {
    if (!hasInitialized) {
      const { section, subsection } = getSectionAndSubsectionForPath(location);
      if (section) {
        setActiveSectionState(section);
        if (subsection) {
          setActiveSubsectionState(subsection);
        }
      }
      setHasInitialized(true);
    }
  }, [location, getSectionAndSubsectionForPath, hasInitialized]);

  /**
   * Set the active section (called when user clicks navigation items)
   */
  const setActiveSection = (section: string) => {
    setActiveSectionState(section);
  };

  /**
   * Set the active subsection (called when user clicks subsection items)
   */
  const setActiveSubsection = (subsection: string) => {
    setActiveSubsectionState(subsection);
  };

  /**
   * Set both active section and subsection (called when user clicks subsection items)
   */
  const setActiveSectionAndSubsection = (
    section: string,
    subsection: string
  ) => {
    setActiveSectionState(section);
    setActiveSubsectionState(subsection);
  };

  const value = {
    activeSection,
    activeSubsection,
    setActiveSection,
    setActiveSubsection,
    setActiveSectionAndSubsection,
  };

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
}
